import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import css from "./SheetDialog.module.css";

/**
 * SheetDialog — управляемый bottom-sheet примитив (порт каркаса шита из
 * design-references/time-picker.js и numpad.js). Открытие — через state
 * хоста (`open`), никаких window-глобалов.
 *
 * Семантику закрытия компонент НЕ решает: backdrop-тап и Esc зовут
 * `onDismiss(reason)`, а commit это или cancel — дело потребителя
 * (TimePicker: dismiss = отмена; NumPad: любое закрытие = commit).
 *
 * Каркас: портал в body, бэкдроп + слайд шита 220мс, handle 24×4,
 * padding-bottom c safe-bottom (в CSS), z-index пропом.
 *
 * Focus-trap (первый в проекте): фокус уводится в шит при открытии,
 * Tab/Shift+Tab циклятся по фокусируемым элементам внутри, при закрытии
 * фокус возвращается на opener. Esc обрабатывает только ВЕРХНИЙ шит
 * стека — NumPad может открываться поверх потребителей TimePicker.
 */
export interface SheetDialogProps {
  open: boolean;
  /** Backdrop-тап, Esc или свайп за handle; commit vs cancel решает потребитель. */
  onDismiss: (reason: "backdrop" | "escape" | "handle") => void;
  children: ReactNode;
  /** z-index host'а; по умолчанию уровень экранных модалок. */
  zIndex?: number | string;
  /** max-height шита (эталоны: TimePicker 55dvh, NumPad 60dvh). */
  maxHeight?: string;
  ariaLabel?: string;
  /** Опц. свайп-вниз за handle → onDismiss("handle") (эталон textpad.js:56-58).
   *  По умолчанию выключен — существующие потребители не меняются ни на пиксель. */
  dragHandle?: boolean;
  /** 12.09 (жалоба оператора «клавиатура всё перекрывает»): поднять шит на эту высоту,
   *  когда снизу выехала docked-клавиатура. Иначе она ложится поверх шита и закрывает
   *  и поле, и кнопки — человек печатает вслепую. 0 — обычное положение у низа. */
  liftBy?: number;
}

/* Стек открытых шитов: Esc закрывает только верхний. */
const sheetStack: symbol[] = [];

/** Открыт ли хоть один шит (БМ5): экранные модалки-хосты глушат свой
 *  document-Esc, пока поверх открыт пад — иначе один Esc закрыл бы всё. */
export function hasOpenSheet(): boolean {
  return sheetStack.length > 0;
}

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SheetDialog({
  open,
  onDismiss,
  children,
  zIndex = "var(--z-modal)",
  maxHeight = "60dvh",
  ariaLabel,
  dragHandle,
  liftBy = 0,
}: SheetDialogProps) {
  const [shown, setShown] = useState(false); // смонтирован в DOM
  const [entered, setEntered] = useState(false); // класс .in (слайд/фейд)
  const [id] = useState(() => Symbol("sheet"));
  const sheetRef = useRef<HTMLDivElement>(null);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  /* Момент появления подложки: щелчок-эхо от касания, которым панель открыли, приходит
     через считанные миллисекунды после него — такой щелчок закрытием не считаем. */
  const shownAt = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;

  /* Маунт с выездом: open → в DOM, .in через rAF; закрытие — снять .in,
     убрать из DOM через 230мс (эталон time-picker.js:152). */
  useEffect(() => {
    if (open) {
      shownAt.current = Date.now();
      setShown(true);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(false);
    const t = window.setTimeout(() => setShown(false), 230);
    return () => window.clearTimeout(t);
  }, [open]);

  /* Фокус: увести в шит, при полном закрытии вернуть на opener. */
  useEffect(() => {
    if (!shown) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sheetRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [shown]);

  /* Членство в стеке — строго пока open: закрывающийся шит (230мс анимации
     выезда) сразу выбывает и отдаёт Esc нижележащему шиту. */
  useEffect(() => {
    if (!open) return;
    sheetStack.push(id);
    return () => {
      const i = sheetStack.indexOf(id);
      if (i !== -1) sheetStack.splice(i, 1);
    };
  }, [open, id]);

  /* Esc: только пока open и только верхний шит стека. */
  useEffect(() => {
    if (!shown) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (sheetStack[sheetStack.length - 1] !== id) return;
      if (openRef.current) dismissRef.current("escape");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [shown, id]);

  /* Свайп-вниз за handle (только при dragHandle): тянем шит пальцем,
     отпустили с dy>70 → onDismiss("handle") (эталон textpad.js:56-58). */
  const dragY = useRef<number | null>(null);
  const onHandleDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragY.current = e.clientY;
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  };
  const onHandleMove = (e: PointerEvent<HTMLDivElement>) => {
    if (dragY.current === null || !sheetRef.current) return;
    sheetRef.current.style.transform = `translateY(${Math.max(0, e.clientY - dragY.current)}px)`;
  };
  const endDrag = () => {
    dragY.current = null;
    const sh = sheetRef.current;
    if (sh) {
      sh.style.transition = "";
      sh.style.transform = "";
    }
  };
  const onHandleUp = (e: PointerEvent<HTMLDivElement>) => {
    if (dragY.current === null) return;
    const dy = e.clientY - dragY.current;
    endDrag();
    if (dy > 70 && openRef.current) dismissRef.current("handle");
  };

  /* Tab-цикл внутри шита. */
  const trapTab = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    const nodes = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) {
      e.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === sheet) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || active === sheet) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!shown) return null;
  return createPortal(
    <div className={`${css.host} ${entered ? css.in : ""}`} style={{ zIndex }}>
      <div
        className={css.backdrop}
        onClick={() => {
          if (!openRef.current) return;
          if (Date.now() - shownAt.current < 350) return;  // эхо касания, открывшего панель
          dismissRef.current("backdrop");
        }}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={css.sheet}
        style={liftBy
          ? { maxHeight: `max(34dvh, calc(${maxHeight} - ${liftBy}px))`, bottom: liftBy, transition: "transform 220ms var(--easing), bottom 180ms var(--easing)" }
          : { maxHeight }}
        onKeyDown={trapTab}
      >
        {dragHandle ? (
          <div
            className={`${css.handle} ${css.drag}`}
            aria-hidden="true"
            onPointerDown={onHandleDown}
            onPointerMove={onHandleMove}
            onPointerUp={onHandleUp}
            onPointerCancel={endDrag}
          />
        ) : (
          <div className={css.handle} aria-hidden="true" />
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
